import React, { useEffect, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

/**
 * Composant pour détecter et afficher l'état offline
 */
export const OfflineDetector: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Afficher un message de reconnexion pendant 3 secondes
      setWasOffline(true);
      setTimeout(() => setWasOffline(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Ne rien afficher si en ligne et pas récemment reconnecté
  if (isOnline && !wasOffline) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-top-2 duration-300">
      {isOnline ? (
        // Message de reconnexion
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl shadow-2xl shadow-green-500/20 flex items-center gap-3 border border-green-400/20">
          <div className="bg-white/20 rounded-full p-1.5">
            <Wifi className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold">Connexion rétablie</p>
            <p className="text-xs text-green-100">Vous êtes de nouveau en ligne</p>
          </div>
        </div>
      ) : (
        // Message offline
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-3 rounded-xl shadow-2xl shadow-orange-500/20 flex items-center gap-3 border border-orange-400/20">
          <div className="bg-white/20 rounded-full p-1.5 animate-pulse">
            <WifiOff className="w-4 h-4" />
          </div>
          <div>
            <p className="font-semibold">Connexion perdue</p>
            <p className="text-xs text-orange-100">Vérifiez votre connexion internet</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Hook pour surveiller l'état de connexion
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};
